import '@/index.css'
import { resolveMarketingEntry } from '@/marketing/routes'

/**
 * The one entry point, and the fork between the two trees (§4w).
 *
 * ## Why the fork is here and not inside a router
 *
 * §4w keeps one React application and two rendering modes. It does **not** keep one bundle: the
 * public website's performance budget — Lighthouse ≥ 90 on a phone — is only reachable because the
 * landing page loads `src/marketing` and `src/shared` and nothing else. A single tree containing
 * both would pull the query client, SignalR and the dashboard into the first paint of the page most
 * visitors see first.
 *
 * So the path decides, before either tree is imported, and the imports are dynamic — which is what
 * makes them two chunks. There is no route on which both are loaded.
 *
 * This file stays deliberately tiny for the same reason: everything it imports is in *both* chunks'
 * critical path.
 *
 * ## `/en/...` is rewritten before anything mounts
 *
 * English lives at the root and `/en/pricing` redirects to `/pricing` permanently. The real 301 is
 * the server's — see `PrerenderedPages` — and this handles the case where no request was made,
 * which is a browser restoring a history entry. Rewriting the address before the first render means
 * the visitor never sees the prefixed URL rather than seeing it flick.
 */
const container = document.getElementById('root')
if (!container) {
  throw new Error('Root element #root was not found in index.html.')
}

const entry = resolveMarketingEntry(window.location.pathname)

if (entry?.redirected) {
  window.history.replaceState(
    null,
    '',
    `${entry.pathname}${window.location.search}${window.location.hash}`,
  )
}

if (entry) {
  void import('@/marketing/mount').then((module) => {
    module.mountMarketing(container, entry.language)
  })
} else {
  void import('@/appMount').then((module) => {
    module.mountApp(container)
  })
}
